import { computed, Injectable, signal } from "@angular/core";
import { httpResource } from "@angular/common/http";
import { UserSettingsModel } from "src/app/v2/pages/contracts/models/user-settings.model";
import { getUserSettings } from "../../../../api/smart-pensjon-rest-api/functions";
import { buildRestApiRootUrl } from "../../../../utils/rest-api-root-url";

@Injectable({
  providedIn: "root",
})
export class SettingsService {
  private readonly restApiRootUrl: string = buildRestApiRootUrl();

  private readonly requested = signal(false);

  /**
   * A request function returning `undefined` leaves the resource idle and issues no request, so a
   * session that needs no user settings never pays for them.
   */
  readonly userSettings = httpResource<UserSettingsModel>(
    () => (this.requested() ? this.restApiRootUrl + getUserSettings.PATH : undefined),
    {
      parse: (raw: unknown): UserSettingsModel => (raw as { payload: UserSettingsModel }).payload,
    },
  );

  // hasIps from user settings. The hasValue() guard is load-bearing, not defensive: `value()` throws
  // ResourceValueError in the resource's error state, and this is read from the Avtaler template.
  readonly hasIps = computed((): boolean => this.userSettings.hasValue() && this.userSettings.value()?.hasIps === true);

  /**
   * hasPublicPension from user settings: public pension held in Storebrand, any contract status.
   * Norsk Pensjon is deliberately NOT part of this attribute — the backend answers for public
   * pension held in Storebrand only.
   */
  readonly hasPublicPension = computed(
    (): boolean => this.userSettings.hasValue() && this.userSettings.value()?.hasPublicPension === true,
  );

  /**
   * Triggers the single shared `/user/settings` request. Idempotent, and safe to call from several
   * consumers: the first caller starts the request and everyone else reads the same resource, so the
   * app fetches user settings at most once.
   */
  public ensureLoaded(): void {
    this.requested.set(true);
  }
}
